"use client";

import { AlertTriangle } from "lucide-react";

/**
 * Visual variant. Only `warning` is supported in V2 — reserved for future
 * tonal variants (info, danger…) if the upload modal needs more.
 */
type WarningBlockVariant = "warning";

/**
 * Props for {@link WarningBlock}.
 */
export interface WarningBlockProps {
  /** Short heading explaining what was detected. */
  title: string;
  /** Optional secondary line, gives the user context. */
  body?: string;
  /** Visual variant. Defaults to `warning`. */
  variant?: WarningBlockVariant;
}

/**
 * Non-blocking notice rendered in the `ready` phase of the upload modal when
 * the quality is degraded but no user input is required to move on
 * (`ok_with_hints` flow, single-track files, OCR caveats, …).
 *
 * Tone is supportive, never blaming. The visual leans on the warm accent
 * token so it reads as a hint and not as an error.
 */
export function WarningBlock({
  title,
  body,
  variant = "warning",
}: WarningBlockProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-variant={variant}
      className="relative flex items-start gap-3"
      style={{
        padding: "12px 16px",
        borderRadius: "var(--radius-card)",
        backgroundColor: "var(--color-accent-warm-subtle)",
        border: "1px solid rgba(251, 191, 36, 0.15)",
      }}
    >
      {/* Left accent bar */}
      <span
        aria-hidden
        className="absolute left-0 rounded-r-full"
        style={{
          width: 2,
          top: 12,
          bottom: 12,
          backgroundColor: "rgba(251, 191, 36, 0.55)",
        }}
      />

      {/* Icon */}
      <span
        aria-hidden
        className="rounded-full flex items-center justify-center shrink-0 mt-px"
        style={{
          width: 22,
          height: 22,
          backgroundColor: "rgba(251, 191, 36, 0.10)",
          border: "1px solid rgba(251, 191, 36, 0.25)",
        }}
      >
        <AlertTriangle
          size={11}
          strokeWidth={2}
          style={{ color: "var(--color-accent-warm)" }}
        />
      </span>

      <div className="flex flex-col gap-0.5 min-w-0">
        <span
          className="text-[13px] font-medium leading-snug"
          style={{ color: "var(--color-text-primary)" }}
        >
          {title}
        </span>
        {body && (
          <span
            className="text-[12px] leading-snug"
            style={{ color: "var(--color-text-secondary)" }}
          >
            {body}
          </span>
        )}
      </div>
    </div>
  );
}
